import { formatInTimeZone } from 'date-fns-tz';
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { cn } from '@apps/web/lib/utils';
import {
    episodeTagLabel,
    getEpisodeTagBadgeClassName,
} from '@apps/web/routes/_app/admin/agent-traces/trace_table';
import {
    type AdminAgentRunExecutionRead,
    type AgentTraceDetail,
} from '@libraries/typescript/api_client';

type TraceEvent = AgentTraceDetail['events'][number];
type TracePendingEvent = AgentTraceDetail['pending_events'][number];
type EpisodeOutcome = NonNullable<AdminAgentRunExecutionRead['outcome']>;
type EpisodeJudgmentItem = NonNullable<
    AdminAgentRunExecutionRead['judgment']
>['items'][number];

const TRIGGER_SNIPPET_MAX_LENGTH = 96;

export type EpisodeTriggerView = {
    readonly eventId: string;
    readonly eventType: string | null;
    readonly label: string;
    // The episode judge's classification of this trigger message, when the
    // episode has been judged and the item matches by inbound event id.
    readonly judgment: EpisodeJudgmentItem | null;
};

export type EpisodeView = {
    readonly runStartedEventId: string;
    readonly ordinal: number;
    readonly startedAt: string;
    // Model attempts within this episode, in attempt order. Provider
    // fallback stamps several executions on one RUN_STARTED marker; they
    // are one episode, not several.
    readonly attempts: ReadonlyArray<AdminAgentRunExecutionRead>;
    // null means the episode predates trigger linkage (unknown, not empty).
    readonly triggers: ReadonlyArray<EpisodeTriggerView> | null;
    readonly continuationReason: string | null;
    readonly outcome: EpisodeOutcome | null;
};

export type InboundMessageCoverage = {
    readonly total: number;
    readonly linked: number;
    readonly unlinked: number;
    readonly queued: number;
    readonly rejected: number;
    // When any episode predates linkage, unlinked messages are expected
    // history rather than a coverage gap.
    readonly hasPreLinkageEpisodes: boolean;
};

function truncateSnippet(text: string): string {
    const collapsed = text.replace(/\s+/g, ' ').trim();
    if (collapsed.length <= TRIGGER_SNIPPET_MAX_LENGTH) {
        return collapsed;
    }
    return `${collapsed.slice(0, TRIGGER_SNIPPET_MAX_LENGTH)}…`;
}

function triggerLabel(event: TraceEvent | undefined, eventId: string): string {
    if (!event) {
        return `Event ${eventId.slice(0, 8)}… (not in this trace window)`;
    }
    const payload = event.payload;
    if (event.type === 'user_message') {
        const text = payload['text'];
        return typeof text === 'string' && text.trim() !== ''
            ? truncateSnippet(text)
            : 'User message';
    }
    if (event.type === 'poke') {
        const reason = payload['reason'];
        return typeof reason === 'string' && reason.trim() !== ''
            ? `Poke: ${truncateSnippet(reason)}`
            : 'Poke';
    }
    if (event.type === 'tool_approval_granted') {
        return 'Tool approval granted';
    }
    return event.type;
}

export function buildEpisodeViews(
    executions: ReadonlyArray<AdminAgentRunExecutionRead>,
    events: ReadonlyArray<TraceEvent>,
): Array<EpisodeView> {
    const eventsById = new Map(events.map((event) => [event.id, event]));
    // The executions read is attempt-grained: provider fallback stamps
    // several executions on one RUN_STARTED marker. Group by marker so an
    // episode renders once, with its attempts inside.
    type EpisodeGroup = {
        readonly firstAttempt: AdminAgentRunExecutionRead;
        readonly attempts: Array<AdminAgentRunExecutionRead>;
    };
    const groups: Array<EpisodeGroup> = [];
    const groupByMarkerId = new Map<string, EpisodeGroup>();
    const sortedExecutions = [...executions].sort(
        (a, b) => a.run_started_event_offset - b.run_started_event_offset,
    );
    for (const execution of sortedExecutions) {
        const existing = groupByMarkerId.get(execution.run_started_event_id);
        if (existing) {
            existing.attempts.push(execution);
            continue;
        }
        const group = { firstAttempt: execution, attempts: [execution] };
        groupByMarkerId.set(execution.run_started_event_id, group);
        groups.push(group);
    }
    return groups.map(({ firstAttempt, attempts }, index) => {
        // Attempts share the marker, so linkage, outcome, and judgment are
        // identical across them; read from the first.
        const triggerEventIds = firstAttempt.trigger_event_ids ?? null;
        const judgmentItemsByEventId = new Map(
            (firstAttempt.judgment?.items ?? [])
                .filter(
                    (item) =>
                        item.inbound_event_id !== null &&
                        item.inbound_event_id !== undefined,
                )
                .map((item) => [item.inbound_event_id, item]),
        );
        return {
            runStartedEventId: firstAttempt.run_started_event_id,
            ordinal: index + 1,
            startedAt: firstAttempt.started_at,
            attempts,
            triggers:
                triggerEventIds === null
                    ? null
                    : triggerEventIds.map((eventId) => {
                          const event = eventsById.get(eventId);
                          return {
                              eventId,
                              eventType: event?.type ?? null,
                              label: triggerLabel(event, eventId),
                              judgment:
                                  judgmentItemsByEventId.get(eventId) ?? null,
                          };
                      }),
            continuationReason: firstAttempt.continuation_reason ?? null,
            outcome: firstAttempt.outcome ?? null,
        };
    });
}

export function buildInboundMessageCoverage(
    executions: ReadonlyArray<AdminAgentRunExecutionRead>,
    events: ReadonlyArray<TraceEvent>,
    pendingEvents: ReadonlyArray<TracePendingEvent>,
): InboundMessageCoverage {
    const linkedEventIds = new Set(
        executions.flatMap((execution) => execution.trigger_event_ids ?? []),
    );
    const userMessageEvents = events.filter(
        (event) => event.type === 'user_message',
    );
    const linked = userMessageEvents.filter((event) =>
        linkedEventIds.has(event.id),
    ).length;
    const pendingUserMessages = pendingEvents.filter(
        (event) => event.type === 'user_message',
    );
    return {
        total: userMessageEvents.length,
        linked,
        unlinked: userMessageEvents.length - linked,
        queued: pendingUserMessages.filter(
            (event) => event.status === 'pending',
        ).length,
        rejected: pendingUserMessages.filter(
            (event) => event.status === 'rejected',
        ).length,
        hasPreLinkageEpisodes: executions.some(
            (execution) =>
                execution.trigger_event_ids === null ||
                execution.trigger_event_ids === undefined,
        ),
    };
}

function outcomeBadge(outcome: EpisodeOutcome | null) {
    if (outcome === null) {
        return <Badge variant="outline">no terminal event</Badge>;
    }
    return (
        <Badge
            variant="secondary"
            className={cn(
                outcome === 'completed' &&
                    'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
                outcome === 'failed' &&
                    'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
                (outcome === 'interrupted' || outcome === 'cancelled') &&
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300',
            )}
        >
            {outcome}
        </Badge>
    );
}

function continuationReasonLabel(reason: string | null): string {
    if (reason === null) {
        return 'continuation';
    }
    return reason.replace(/_/g, ' ');
}

export function AgentTraceEpisodesPanel({
    trace,
    organizationTimezone,
    isPartialHistory = false,
}: {
    readonly trace: Pick<
        AgentTraceDetail,
        'executions' | 'events' | 'pending_events'
    >;
    readonly organizationTimezone: string;
    readonly isPartialHistory?: boolean;
}) {
    const allEpisodes = useMemo(
        () => buildEpisodeViews(trace.executions, trace.events),
        [trace.executions, trace.events],
    );
    const episodes = useMemo(() => {
        if (!isPartialHistory) {
            return allEpisodes;
        }
        const loadedEventIds = new Set(trace.events.map((event) => event.id));
        return allEpisodes.filter((episode) =>
            loadedEventIds.has(episode.runStartedEventId),
        );
    }, [allEpisodes, isPartialHistory, trace.events]);
    const coverage = useMemo(
        () =>
            buildInboundMessageCoverage(
                trace.executions,
                trace.events,
                trace.pending_events,
            ),
        [trace.executions, trace.events, trace.pending_events],
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">Requests</CardTitle>
                {isPartialHistory ? (
                    <div className="text-muted-foreground text-xs">
                        Showing requests from the loaded event history.
                    </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="outline">
                        {coverage.total} inbound message
                        {coverage.total === 1 ? '' : 's'}
                    </Badge>
                    <Badge
                        variant="secondary"
                        className={cn(
                            coverage.total > 0 &&
                                coverage.linked === coverage.total &&
                                'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
                        )}
                    >
                        {coverage.linked} linked
                    </Badge>
                    {coverage.unlinked > 0 ? (
                        <Badge
                            variant="secondary"
                            className={cn(
                                !coverage.hasPreLinkageEpisodes &&
                                    'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
                            )}
                        >
                            {coverage.unlinked}{' '}
                            {coverage.hasPreLinkageEpisodes
                                ? 'pre-linkage'
                                : 'unlinked'}
                        </Badge>
                    ) : null}
                    {coverage.queued > 0 ? (
                        <Badge variant="secondary">
                            {coverage.queued} queued
                        </Badge>
                    ) : null}
                    {coverage.rejected > 0 ? (
                        <Badge
                            variant="secondary"
                            className="bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300"
                        >
                            {coverage.rejected} rejected
                        </Badge>
                    ) : null}
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {episodes.length === 0 ? (
                    <div className="text-muted-foreground py-4 text-center text-sm">
                        No executions yet.
                    </div>
                ) : (
                    episodes.map((episode) => (
                        <div
                            key={episode.runStartedEventId}
                            className="rounded-lg border p-3"
                            data-testid="agent-trace-episode"
                        >
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="font-medium">
                                    #{episode.ordinal}
                                </span>
                                {outcomeBadge(episode.outcome)}
                                <span className="text-muted-foreground text-xs">
                                    {formatInTimeZone(
                                        new Date(episode.startedAt),
                                        organizationTimezone,
                                        'MMM d, h:mm:ss a',
                                    )}
                                </span>
                                {episode.attempts.map((attempt, index) => (
                                    <span
                                        key={attempt.execution_id}
                                        className="text-muted-foreground inline-flex items-center gap-1 text-xs"
                                        data-testid="agent-trace-episode-attempt"
                                    >
                                        {episode.attempts.length > 1
                                            ? `Attempt ${index + 1}: `
                                            : null}
                                        {attempt.model ?? 'unknown model'}
                                        {attempt.braintrust_url ? (
                                            <a
                                                href={attempt.braintrust_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label={`Open episode ${episode.ordinal} attempt ${index + 1} in Braintrust`}
                                                className="hover:text-foreground"
                                            >
                                                <ExternalLink className="size-3.5" />
                                            </a>
                                        ) : null}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-2 space-y-1">
                                {episode.triggers === null ? (
                                    <div className="text-muted-foreground text-xs">
                                        Pre-linkage episode — no trigger data
                                        recorded.
                                    </div>
                                ) : episode.triggers.length === 0 ? (
                                    <Badge variant="outline">
                                        Continuation —{' '}
                                        {continuationReasonLabel(
                                            episode.continuationReason,
                                        )}
                                    </Badge>
                                ) : (
                                    episode.triggers.map((trigger) => (
                                        <div
                                            key={trigger.eventId}
                                            className="bg-muted/50 rounded-md px-2 py-1 text-xs"
                                            data-testid="agent-trace-episode-trigger"
                                        >
                                            <span className="text-muted-foreground mr-1">
                                                {trigger.eventType ===
                                                'user_message'
                                                    ? 'Message:'
                                                    : 'Trigger:'}
                                            </span>
                                            {trigger.label}
                                            {trigger.judgment ? (
                                                <span
                                                    className="mt-1 flex flex-wrap items-center gap-1"
                                                    title={
                                                        trigger.judgment
                                                            .summary ??
                                                        undefined
                                                    }
                                                    data-testid="agent-trace-episode-judgment"
                                                >
                                                    <Badge
                                                        variant="secondary"
                                                        className={cn(
                                                            'text-xs',
                                                            getEpisodeTagBadgeClassName(
                                                                trigger.judgment,
                                                            ),
                                                        )}
                                                    >
                                                        {episodeTagLabel(
                                                            trigger.judgment,
                                                        )}
                                                    </Badge>
                                                    {trigger.judgment
                                                        .agent_behavior ===
                                                    'failure' ? (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-rose-100 text-xs text-rose-800 dark:bg-rose-950/50 dark:text-rose-300"
                                                        >
                                                            behavior: failure
                                                        </Badge>
                                                    ) : null}
                                                    {(
                                                        trigger.judgment
                                                            .failure_tags ?? []
                                                    ).map((tag) => (
                                                        <Badge
                                                            key={tag}
                                                            variant="outline"
                                                            className="text-xs"
                                                        >
                                                            {tag.replace(
                                                                /-/g,
                                                                ' ',
                                                            )}
                                                        </Badge>
                                                    ))}
                                                </span>
                                            ) : null}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    ))
                )}
            </CardContent>
        </Card>
    );
}
